// src/components/CognixAgentList.jsx
import React, { useState, useEffect } from 'react';
import { 
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { cognixAgentService } from '../services/cognixAgentService';
import './CognixAgentList.css';

const CognixAgentList = ({ onChatClick, onEditClick, refreshTrigger }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await cognixAgentService.listAgents();
      setAgents(response.agents || []);
    } catch (error) {
      console.error('Error loading Cognix agents:', error);
      setError('Failed to load agents: ' + error.message);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, [refreshTrigger]);

  const handleDeleteAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agent.agent_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleteLoading(agent.agent_id);
      await cognixAgentService.deleteAgent(agent.agent_id);
      setAgents(prev => prev.filter(a => a.agent_id !== agent.agent_id));
    } catch (error) {
      setError('Failed to delete agent: ' + error.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const getToolCount = (agent) => {
    return agent.tool_count || 0;
  };

  if (loading) {
    return (
      <div className="agent-list-loading">
        <div className="spinner"></div>
        <span>Loading Cognix agents...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-list-error">
        <ExclamationTriangleIcon className="w-5 h-5" />
        <span>{error}</span>
        <button onClick={loadAgents} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="no-agents">
        <div className="flex justify-center mb-4">
          <CpuChipIcon className="w-16 h-16 text-gray-400" />
        </div>
        <h3>No Cognix Agents Created Yet</h3>
        <p>Create your first Cognix agent to get started with dynamic AI tools.</p>
      </div>
    );
  }

  return (
    <div className="agent-list">
      <div className="agent-list-header">
        <h3>Your Cognix Agents ({agents.length})</h3>
      </div>
      
      {/* Tabular View */}
      <div className="agent-table-container">
        <table className="agent-table">
          <thead>
            <tr>
              <th className="table-header">#</th>
              <th className="table-header">Agent Name</th>
              <th className="table-header">AI Role</th>
              <th className="table-header">Tools</th>
              <th className="table-header">Created</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent, index) => (
              <tr key={agent.agent_id} className="agent-row">
                <td className="table-cell serial-number">{index + 1}</td>
                <td className="table-cell agent-name-cell">
                  <button
                    className="agent-name-link"
                    onClick={() => onChatClick && onChatClick(agent)}
                    title="Click to chat with this agent"
                  >
                    {agent.agent_name}
                  </button>
                </td>
                <td className="table-cell ai-role-cell">
                  <div className="ai-role-text" title={agent.ai_role}>
                    {agent.ai_role?.substring(0, 60)}
                    {agent.ai_role?.length > 60 && '...'}
                  </div>
                </td>
                <td className="table-cell tools-cell">
                  <span className="count-badge tools">
                    {getToolCount(agent)}
                  </span>
                </td>
                <td className="table-cell date-cell">
                  {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="table-cell actions-cell">
                  <div className="action-buttons">
                    <button 
                      className="btn-chat"
                      onClick={() => onChatClick && onChatClick(agent)}
                      title="Chat with this agent"
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    </button>
                    
                    <button 
                      className="btn-edit"
                      onClick={() => onEditClick && onEditClick(agent)}
                      title="Edit agent"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteAgent(agent)}
                      disabled={deleteLoading === agent.agent_id}
                      title="Delete agent"
                    >
                      {deleteLoading === agent.agent_id ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CognixAgentList;
